import * as React from "react";
import { useIcon } from "@salt-ds/core";

import { StatusAdornment } from "../display/StatusAdornment";
import { useControlled } from "./useControlled";
import { useForkRef } from "./useForkRef";
import { Pill } from "./Pill";
import { useFormFieldProps } from "../salt/form-field-context";
import { useTruncatePills } from "./useTruncatePills";
import "./pill-input.css";

/**
 * PillInput — a text field that also holds removable chips. The input half of `ComboBox`.
 *
 * ── THE PILLS SIT BESIDE THE INPUT, NOT INSIDE IT ─────────────────────────────────────────
 *
 * An `<input>` holds text and nothing else, so the pills live in a sibling `role="list"` and the
 * two are made to look like one field by the wrapper's border. That is not a workaround: each
 * pill is a real `<button>`, and a button inside a text field is not something a browser will
 * render or a screen reader will describe.
 *
 * ── BACKSPACE ONLY REMOVES A PILL FROM A COLLAPSED CARET AT POSITION 0 ────────────────────
 *
 * Both conditions matter. `selectionStart === 0` alone is true while a user has selected text
 * from the start, and deleting a pill instead of the selected text would be destructive and
 * surprising. Requiring `selectionEnd === 0` too means the caret is genuinely at the beginning
 * with nothing selected — the only moment where "delete backwards" can mean the pill.
 */
export type PillInputVariant = "primary" | "secondary" | "tertiary";
export type PillInputValidationStatus = "error" | "warning" | "success";

export interface PillInputProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange"> {
  bordered?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  /** Shown instead of an empty value when read-only, so a blank field is never mistaken for a failed load. */
  emptyReadOnlyMarker?: string;
  endAdornment?: React.ReactNode;
  hidePillClose?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onPillRemove?: (event: React.SyntheticEvent, index: number) => void;
  pills?: string[];
  placeholder?: string;
  readOnly?: boolean;
  role?: string;
  startAdornment?: React.ReactNode;
  textAlign?: "left" | "center" | "right";
  truncate?: boolean;
  validationStatus?: PillInputValidationStatus;
  value?: string;
  variant?: PillInputVariant;
}

export const PillInput = React.forwardRef<HTMLDivElement, PillInputProps>(function PillInput(
  {
    "aria-activedescendant": ariaActiveDescendant,
    "aria-expanded": ariaExpanded,
    "aria-owns": ariaOwns,
    bordered = false,
    className,
    defaultValue: defaultValueProp,
    disabled,
    emptyReadOnlyMarker = "—",
    endAdornment,
    hidePillClose,
    id: idProp,
    inputProps = {},
    inputRef: inputRefProp,
    onChange,
    onPillRemove,
    pills = [],
    placeholder,
    readOnly: readOnlyProp,
    role,
    startAdornment,
    style,
    textAlign = "left",
    truncate,
    validationStatus: validationStatusProp,
    value: valueProp,
    variant = "primary",
    ...rest
  },
  ref,
) {
  const { CloseIcon, OverflowIcon } = useIcon();
  const {
    a11yProps,
    disabled: formFieldDisabled,
    necessity: formFieldNecessity,
    readOnly: formFieldReadOnly,
    validationStatus: formFieldValidationStatus,
  } = useFormFieldProps();

  const isDisabled = disabled || formFieldDisabled;
  const isReadOnly = readOnlyProp || formFieldReadOnly;
  const validationStatus = (formFieldValidationStatus ??
    validationStatusProp) as PillInputValidationStatus | undefined;

  const [focused, setFocused] = React.useState(false);
  const [focusedPillIndex, setFocusedPillIndex] = React.useState(-1);

  const isEmptyReadOnly = isReadOnly && !defaultValueProp && !valueProp;
  const defaultValue = isEmptyReadOnly
    ? emptyReadOnlyMarker
    : (defaultValueProp ?? (valueProp === undefined ? "" : undefined));

  const {
    "aria-describedby": inputDescribedBy,
    "aria-labelledby": inputLabelledBy,
    className: inputClassName,
    onBlur,
    onChange: _ignoredInputOnChange,
    onFocus,
    onKeyDown: inputOnKeyDown,
    required: inputRequired,
    ...restInputProps
  } = inputProps;

  const isRequired = formFieldNecessity
    ? ["required", "asterisk"].includes(formFieldNecessity)
    : inputRequired;

  const [value, setValue] = useControlled<string>({
    controlled: valueProp,
    default: defaultValue ?? "",
  });

  const { pillListRef, visiblePills } = useTruncatePills({
    enable: truncate && pills.length > 0,
    pills,
  });

  const reactId = React.useId();
  const id = idProp ?? reactId;
  const pillListId = `${id}-optionsList`;
  const pillElementsRef = React.useRef<(HTMLButtonElement | null)[]>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const handleInputRef = useForkRef(inputRef, inputRefProp);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    // See the header: BOTH ends of the selection must be at 0, or this deletes a pill while the
    // user is deleting selected text.
    if (target.selectionStart === 0 && target.selectionEnd === 0) {
      const lastIndex = pills.length - 1;
      if (event.key === "Backspace" && pills[lastIndex]) {
        event.preventDefault();
        onPillRemove?.(event, lastIndex);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        pillElementsRef.current[lastIndex]?.focus();
      }
    }
    inputOnKeyDown?.(event);
  };

  const handlePillKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const index = Number(event.currentTarget.dataset.index);
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      pillElementsRef.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      // Past the last pill is the input, not nothing: the row reads left to right as one field.
      if (index === pills.length - 1) inputRef.current?.focus();
      else pillElementsRef.current[index + 1]?.focus();
    } else if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onPillRemove?.(event, index);
      /*
       * Focus has to be placed deliberately, because the element that had it is about to be
       * removed and focus would otherwise fall back to the body — losing the user's place
       * entirely. Removing the last pill goes to the input; removing the final pill of several
       * goes to its new neighbour; anything else stays at the same index, which is now the pill
       * that shifted into the gap.
       */
      if (pills.length === 1) inputRef.current?.focus();
      else if (index === pills.length - 1) pillElementsRef.current[pills.length - 2]?.focus();
      else pillElementsRef.current[index]?.focus();
    }
  };

  const classes = ["saltPillInput", `saltPillInput-${variant}`];
  if (!isDisabled && focused) classes.push("saltPillInput-focused");
  if (isDisabled) classes.push("saltPillInput-disabled");
  if (isReadOnly) classes.push("saltPillInput-readOnly");
  if (truncate) classes.push("saltPillInput-truncate");
  if (validationStatus) classes.push(`saltPillInput-${validationStatus}`);
  if (bordered) classes.push("saltPillInput-bordered");
  if (className) classes.push(className);

  return (
    <div
      className={classes.join(" ")}
      ref={ref}
      style={{ "--input-textAlign": textAlign, ...style } as React.CSSProperties}
      {...rest}
    >
      {startAdornment && (
        <div className="saltPillInput-startAdornmentContainer">{startAdornment}</div>
      )}
      <div className="saltPillInput-inputWrapper" ref={pillListRef}>
        <div
          aria-label="Selected Options"
          aria-labelledby={[a11yProps?.["aria-labelledby"], pillListId].filter(Boolean).join(" ")}
          className="saltPillInput-pillList"
          id={pillListId}
          role="list"
        >
          {visiblePills.map((pill, index) => (
            <div key={pill} role="listitem">
              <Pill
                aria-label={hidePillClose ? pill : `Remove ${pill}`}
                data-index={index}
                disabled={disabled}
                onClick={(event) => {
                  if (!hidePillClose) {
                    onPillRemove?.(event, Number(event.currentTarget.dataset.index));
                  }
                  // Focus returns to the input either way: after removing a chip the user is
                  // still typing, and leaving focus on a button that just vanished is worse.
                  inputRef.current?.focus();
                }}
                onFocus={() => setFocusedPillIndex(index)}
                onKeyDown={handlePillKeyDown}
                ref={(element) => {
                  pillElementsRef.current[index] = element;
                }}
                /*
                 * A roving tabindex. The pills are one composite widget, so Tab enters the group
                 * once and the arrow keys move within it — Tab stopping at every chip in a
                 * ten-selection field is what makes such a field unusable by keyboard.
                 */
                tabIndex={focusedPillIndex === -1 || focusedPillIndex === index ? 0 : -1}
              >
                {pill}
                {!hidePillClose && <CloseIcon aria-hidden />}
              </Pill>
            </div>
          ))}
          {visiblePills.length < pills.length && (
            <div role="listitem">
              <div className="saltPillInput-overflowIndicator" data-overflowindicator>
                <OverflowIcon aria-hidden />
              </div>
            </div>
          )}
        </div>
        <input
          aria-activedescendant={ariaActiveDescendant}
          aria-describedby={
            [a11yProps?.["aria-describedby"], inputDescribedBy].filter(Boolean).join(" ") ||
            undefined
          }
          aria-expanded={ariaExpanded}
          aria-labelledby={
            [a11yProps?.["aria-labelledby"], inputLabelledBy].filter(Boolean).join(" ") || undefined
          }
          aria-owns={ariaOwns}
          // Off because the field already offers its own list: a browser dropdown on top of the
          // component's own is two competing menus over one input.
          autoComplete="off"
          className={["saltPillInput-input", inputClassName].filter(Boolean).join(" ")}
          disabled={isDisabled}
          id={id}
          onBlur={(event) => {
            onBlur?.(event);
            setFocused(false);
          }}
          onChange={(event) => {
            setValue(event.target.value);
            onChange?.(event);
          }}
          onFocus={
            !isDisabled
              ? (event) => {
                  onFocus?.(event);
                  setFocused(true);
                }
              : undefined
          }
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          readOnly={isReadOnly}
          ref={handleInputRef}
          required={isRequired}
          role={role}
          tabIndex={isDisabled ? -1 : 0}
          value={value}
          {...restInputProps}
        />
      </div>
      {!isDisabled && validationStatus && (
        <div className="saltPillInput-statusAdornmentContainer">
          <StatusAdornment status={validationStatus} />
        </div>
      )}
      {endAdornment && <div className="saltPillInput-endAdornmentContainer">{endAdornment}</div>}
      <div className="saltPillInput-activationIndicator" />
    </div>
  );
});
